import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  AuthClearStorage,
  AuthError,
  AuthInit,
  AuthLogin,
  AuthLogout,
  AuthLogoutWithSkid,
  AuthRedirectUserToChangePassword,
  AuthRedirectUserToDashboard,
  AuthResetPassword,
  AuthSetExpiresAt,
  AuthSetInitialState,
  AuthSetUser,
  AuthSSOLogin,
} from './auth.actions';
import { catchError, finalize, map, switchMap, take, tap } from 'rxjs/operators';
import { AuthenticationIdpType, AuthUserData, LoginData, UserDataRole } from './models';
import { AuthService } from './auth.service';
import { LocalStorageService } from '../../services/local-storage.service';
import { Inject, Injectable } from '@angular/core';
import { RouterFacade } from '../router/router.facade';
import { NotificationService } from '../../../common/services';
import { CustomHttpErrorResponse, Error, Language, LocalStorageKey } from '../../../common/constants';
import { Observable, of } from 'rxjs';
import { PermissionModel } from '../../../common/models';
import { UserOwnerType } from '../administration/user/constants';
import { parseQuery } from '../../../common/utils';
import { DOCUMENT } from '@angular/common';
import { ROOT_ENV, SKID_CHINA_URL, SKID_URL } from '../../../services';
import { HttpErrorResponse } from '@angular/common/http';
import { ProfileSetFromAuthMe } from '../administration';
import { parseUtcDateStringToMs } from './auth.utils';

export interface AuthStateModel {
  data: AuthUserData;
  skidUrl: string;
  expiresAt: number | null;
  isMeLoading: boolean;
}

const initialState: AuthStateModel = {
  data: null,
  skidUrl: null,
  expiresAt: null,
  isMeLoading: false,
};

@State<AuthStateModel>({
  name: 'auth',
  defaults: initialState,
})
@Injectable()
export class AuthState {
  constructor(
    private authService: AuthService,
    private routerFacade: RouterFacade,
    private localStorageService: LocalStorageService,
    private notificationService: NotificationService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  @Selector()
  static getState(state: AuthStateModel): AuthStateModel {
    return state;
  }

  @Selector()
  static getUserData(state: AuthStateModel): AuthUserData {
    return state.data;
  }

  @Selector()
  static getUserId(state: AuthStateModel): number | null {
    return state.data?.id ?? null;
  }

  @Selector()
  static getPermissions(state: AuthStateModel): PermissionModel[] | null {
    return state.data?.permissions ?? null;
  }

  @Selector()
  static getRoles(state: AuthStateModel): UserDataRole[] {
    return state.data?.roles ?? [];
  }

  @Selector()
  static getOwnerType(state: AuthStateModel): UserOwnerType | null {
    return state.data?.ownerType ?? null;
  }

  @Selector()
  static getDefaultLanguage(state: AuthStateModel): Language {
    return state.data?.defaultLanguage;
  }

  @Selector()
  static getIsTwentyFourHourTime(state: AuthStateModel): boolean | null {
    return state.data?.twentyFourHourTime;
  }

  @Selector()
  static getCountry(state: AuthStateModel): string {
    return state.data?.country;
  }

  @Selector()
  static getTimezone(state: AuthStateModel): string {
    return state.data?.timezone;
  }

  @Selector()
  static getRenderTimezone(state: AuthStateModel): string {
    return state.data?.renderTimezone;
  }

  @Selector()
  static isInternalUser(state: AuthStateModel): boolean {
    return state.data?.isInternalUser;
  }

  @Selector()
  static getAuthenticationIdp(state: AuthStateModel): AuthenticationIdpType {
    return state.data?.authenticationIdp;
  }

  @Selector()
  static getSkidUrl(state: AuthStateModel): string {
    return state?.skidUrl;
  }

  @Selector()
  static getExpiresAt(state: AuthStateModel): number | null {
    return state.expiresAt;
  }

  @Action(AuthLogin)
  authLogin(ctx: StateContext<AuthStateModel>, { payload }: AuthLogin): Observable<void> {
    ctx.patchState({ isMeLoading: true });

    return this.authService.login(payload).pipe(
      switchMap(() => this.authService.me()),
      tap((loginData: LoginData) => {
        ctx.dispatch(new ProfileSetFromAuthMe(loginData.dto));
        ctx.dispatch(new AuthSetUser(loginData.response));
        if (loginData.response.passwordExpired) {
          ctx.dispatch(new AuthRedirectUserToChangePassword());
        } else {
          ctx.dispatch(new AuthRedirectUserToDashboard());
        }
      }),
      map(() => void 0),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => ctx.dispatch(new AuthError(error))),
      finalize(() => ctx.patchState({ isMeLoading: false })),
    );
  }

  @Action(AuthSSOLogin)
  authSSOLogin(ctx: StateContext<AuthStateModel>, { payload }: AuthSSOLogin): Observable<void> {
    ctx.patchState({ isMeLoading: true });
    return this.authService.loginByHash(payload).pipe(
      switchMap(() => this.authService.me()),
      tap((loginData: LoginData) => {
        ctx.dispatch(new ProfileSetFromAuthMe(loginData.dto));
        ctx.dispatch(new AuthSetUser(loginData.response));
        ctx.dispatch(new AuthRedirectUserToDashboard());
      }),
      map(() => void 0),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.redirectToLogin();
        return ctx.dispatch(new AuthError(error));
      }),
      finalize(() => ctx.patchState({ isMeLoading: false })),
    );
  }

  @Action(AuthInit)
  authInit(ctx: StateContext<AuthStateModel>): Observable<void> {
    const state = ctx.getState();
    if (state.data || state.isMeLoading) {
      return of(void 0);
    }

    ctx.patchState({ isMeLoading: true });

    return this.authService.me().pipe(
      tap((loginData: LoginData) => {
        ctx.dispatch(new ProfileSetFromAuthMe(loginData.dto));
        ctx.dispatch(new AuthSetUser(loginData.response));
      }),
      map(() => void 0),
      catchError((err: HttpErrorResponse) => {
        if (err.status === 401) {
          ctx.dispatch(new AuthSetInitialState());
          return of(void 0);
        }
        ctx.dispatch(new AuthError((err.error as Error) ?? (err as unknown as Error)));
        return of(void 0);
      }),
      finalize(() => ctx.patchState({ isMeLoading: false })),
    );
  }

  @Action(AuthResetPassword)
  authResetPassword(ctx: StateContext<AuthStateModel>, { payload }: AuthResetPassword): Observable<void> {
    return this.authService.resetPassword(payload).pipe(
      catchError(({ error }: CustomHttpErrorResponse<Error>) => {
        this.notificationService.error(error.message);
        throw error;
      }),
    );
  }

  @Action(AuthSetUser)
  authSetUser(ctx: StateContext<AuthStateModel>, { payload }: AuthSetUser): void {
    const { expiresAt, ...userData } = payload;
    ctx.patchState({
      data: userData as AuthUserData,
      skidUrl: this.resolveSkidUrl(payload.authenticationIdp),
    });
    ctx.dispatch(new AuthSetExpiresAt(parseUtcDateStringToMs(expiresAt)));
  }

  @Action(AuthSetExpiresAt)
  setExpiresAt(ctx: StateContext<AuthStateModel>, { expiresAt }: AuthSetExpiresAt): void {
    ctx.patchState({ expiresAt });
  }

  @Action(AuthLogout)
  authLogout(ctx: StateContext<AuthStateModel>, { payload }: AuthLogout): Observable<void> {
    return this.authService.logout().pipe(
      tap(() => this.handleLogoutSuccess(ctx, payload)),
      catchError(() => {
        this.handleLogoutSuccess(ctx, payload);
        return of(void 0);
      }),
    );
  }

  @Action(AuthLogoutWithSkid)
  authLogoutWithSkid(ctx: StateContext<AuthStateModel>): void {
    const skidUrlValue = ctx.getState().skidUrl;
    if (!skidUrlValue) {
      ctx.dispatch(new AuthLogout());
      return;
    }
    this.redirectToSkidLogin(`${skidUrlValue}/auth/connect/endsession?post_logout_redirect_uri=${ROOT_ENV}`);
    ctx.dispatch(new AuthClearStorage());
  }

  @Action(AuthClearStorage)
  clearLocalStorage(ctx: StateContext<AuthStateModel>): Observable<void> {
    return this.localStorageService.removeData$([LocalStorageKey.RedirectUrl]).pipe(
      tap(() => {
        ctx.dispatch(new AuthSetInitialState());
      }),
      map(() => void 0), // Map to void to match the expected return type.
    );
  }

  @Action(AuthRedirectUserToDashboard)
  authRedirectUserToDashboard(): Observable<void> {
    return this.localStorageService.getData$(LocalStorageKey.RedirectUrl).pipe(
      take(1),
      switchMap((redirectUrl: string | null) => {
        const path = redirectUrl || '/dashboard';
        const [pathString, queryParamsString] = path.split('?');

        this.routerFacade.go({
          path: [pathString],
          queryParams: queryParamsString ? parseQuery(queryParamsString) : undefined,
        });

        // If there was a redirectUrl, we need to remove it from local storage.
        if (redirectUrl) {
          return this.localStorageService.removeData$([LocalStorageKey.RedirectUrl]);
        }

        // If there was no redirectUrl, there's nothing to remove.
        return of(null);
      }),
      map(() => void 0),
    );
  }

  @Action(AuthRedirectUserToChangePassword)
  authRedirectUserToChangePassword(): void {
    this.routerFacade.go({ path: ['/change-password'] });
  }

  @Action(AuthError)
  authError(ctx: StateContext<AuthStateModel>, { payload }: AuthError): void {
    if (payload?.message) {
      this.notificationService.error(payload.message);
    }
  }

  @Action(AuthSetInitialState)
  setInitialState(ctx: StateContext<AuthStateModel>): void {
    ctx.setState(initialState);
  }

  private handleLogoutSuccess(ctx: StateContext<AuthStateModel>, redirectUrl?: string): void {
    ctx.dispatch(new AuthSetInitialState());
    this.redirectToLogin(redirectUrl);
  }

  private redirectToSkidLogin(url: string): void {
    this.document.defaultView?.open(url, '_self');
  }

  private redirectToLogin(redirectUrl: string = ''): void {
    this.localStorageService
      .setData$({ [LocalStorageKey.RedirectUrl]: redirectUrl })
      .pipe(take(1))
      .subscribe();
    this.document.defaultView.location.href = ROOT_ENV;
  }

  private resolveSkidUrl(idp: AuthenticationIdpType): string | null {
    if (idp === AuthenticationIdpType.SKID) {
      return SKID_URL;
    }
    if (idp === AuthenticationIdpType.SKIDCN) {
      return SKID_CHINA_URL;
    }
    return null;
  }
}
